import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, X } from "lucide-react";
import { http } from "@/lib/api";

/**
 * Compact header search. Debounces the query, hits /api/pixels/search, shows
 * a dropdown of matching blocks. Selecting a result pans the grid canvas to
 * its coordinates (via a window event the PixelGrid subscribes to) and
 * triggers a purple ambient flash on the matched block.
 */
export default function HeaderSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Debounced search
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await http.get("/pixels/search", { params: { q, limit: 8 } });
        if (!cancelled) { setResults(data.results || []); setOpen(true); }
      } catch { /* silent */ }
      finally { if (!cancelled) setBusy(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pickBlock = (block) => {
    setOpen(false);
    setQ(block.brand_name || "");
    // If we're not on the landing page, navigate first so the grid mounts.
    if (location.pathname !== "/") {
      navigate("/#explore");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("pixel:pan-to", { detail: { block } }));
        document.querySelector("#explore")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    } else {
      window.dispatchEvent(new CustomEvent("pixel:pan-to", { detail: { block } }));
      document.querySelector("#explore")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div ref={boxRef} className="relative flex-1 max-w-xs" data-testid="header-search">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] focus-within:border-violet-400/50 transition">
        <Search className="w-3.5 h-3.5 text-[var(--fg-muted)] shrink-0" />
        <input
          data-testid="header-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder="Find a brand on the grid…"
          className="bg-transparent outline-none text-sm w-full placeholder:text-[var(--fg-muted)]"
          aria-label="Search brands on the pixel grid"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setResults([]); setOpen(false); }}
            data-testid="header-search-clear"
            className="p-0.5 rounded-full hover:bg-white/10"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5 text-[var(--fg-muted)]" />
          </button>
        )}
      </div>

      {open && (
        <div
          data-testid="header-search-results"
          className="absolute top-full left-0 right-0 mt-2 rounded-xl border border-white/[0.08] bg-[rgba(10,10,16,0.96)] backdrop-blur-xl shadow-2xl max-h-96 overflow-y-auto"
          style={{ boxShadow: "0 24px 60px rgba(0,0,0,0.6), 0 0 40px rgba(139,92,246,0.1)" }}
        >
          {busy && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-[var(--fg-muted)]">Searching…</div>
          )}
          {!busy && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-[var(--fg-muted)]">No brands match “{q}”.</div>
          )}
          {results.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => pickBlock(b)}
              data-testid={`header-result-${b.id}`}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-violet-500/10 transition text-left"
            >
              <div className="w-8 h-8 rounded-md overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
                {b.image_url
                  ? <img src={b.image_url} alt="" className="w-full h-full object-contain" />
                  : <div className="w-full h-full bg-gradient-to-br from-violet-500 to-cyan-400" />}
              </div>
              <div className="min-w-0 grow">
                <div className="font-medium text-sm truncate">{b.brand_name || "Advertiser"}</div>
                <div className="text-xs text-[var(--fg-muted)] font-mono truncate">
                  ({b.x}, {b.y}) · {b.width}×{b.height}
                </div>
              </div>
              <span className="pill text-[10px] shrink-0">{b.tier}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
